import React, { useRef, useEffect, useState, useMemo } from 'react';
import clsx from 'clsx';
import Image from 'next/image';
import { Button } from '~/shared/components/Button/Button';
import Typography from '~/shared/components/Typography/Typography';
import { useTranslation } from '~/shared/utils/translation';
import type { TranslationKey } from '~/lib/data-contract';
import TicketShapeSVG from './TicketShape';
import styles from './VouchersCard.module.scss';

const DEFAULT_VOUCHER_IMAGE = '/assets/voucher-default.png';

export interface VouchersCardProps {
    id: string;
    title: string;
    description: string;
    expiryDate: string;
    badgeText?: string;
    imageSrc?: string;
    imageAlt?: string;
    redeemLabel?: string;
    onRedeem?: (id: string) => void;
    className?: string;
    variant?: 'normal' | 'lite';
    disabledButton?: boolean;
}

const VouchersCard: React.FC<VouchersCardProps> = ({
    id,
    title,
    description,
    expiryDate,
    badgeText,
    imageSrc,
    imageAlt = 'Voucher illustration',
    redeemLabel = 'Indløs',
    onRedeem,
    className,
    variant = 'normal',
    disabledButton,
}) => {
    const { translate } = useTranslation();
    const cardRef = useRef<HTMLDivElement>(null);
    const [cardHeight, setCardHeight] = useState<number>(0);
    const [cardWidth, setCardWidth] = useState<number>(0);
    const [currentImageSrc, setCurrentImageSrc] = useState<string>(() => {
        // Determine initial image source, fallback to default if not provided or empty
        return imageSrc && imageSrc.trim() !== '' ? imageSrc : DEFAULT_VOUCHER_IMAGE;
    });

    // Get card dimensions for proper SVG sizing
    useEffect(() => {
        if (cardRef.current) {
            const updateSize = () => {
                const { offsetHeight, offsetWidth } = cardRef.current!;
                setCardHeight(offsetHeight || 180);
                setCardWidth(offsetWidth || 400);
            };

            // Initial update
            updateSize();

            // Update on resize
            window.addEventListener('resize', updateSize);
            return () => window.removeEventListener('resize', updateSize);
        }
    }, []);

    // Update current image source when imageSrc prop changes
    useEffect(() => {
        if (imageSrc && imageSrc.trim() !== '') {
            setCurrentImageSrc(imageSrc);
        } else {
            setCurrentImageSrc(DEFAULT_VOUCHER_IMAGE);
        }
    }, [imageSrc]);

    const handleRedeem = () => {
        if (onRedeem) {
            onRedeem(id);
        }
    };

    // Calculate the notch position at 80% of card height
    const notchPosition = Math.floor(cardHeight * 0.8);

    // Memoize badge text to avoid recalculating on every render
    const displayBadgeText = useMemo(() => {
        if (!badgeText) return null;
        // If it contains a dot, treat as translation key, otherwise use as plain text
        return badgeText.includes('.')
            ? translate(badgeText as TranslationKey) || badgeText
            : badgeText;
    }, [badgeText]);

    return (
        <div className={clsx(styles.card, styles[`card--${variant}`], className)} ref={cardRef}>
            {/* SVG background with ticket shape */}
            <div className={styles.svgContainer}>
                <TicketShapeSVG
                    id={id}
                    width={cardWidth}
                    height={cardHeight}
                    notchPosition={notchPosition}
                />
            </div>

            <div className={styles.cardContent}>
                <div className={styles.contentWrapper}>
                    {displayBadgeText && <div className={styles.badge}>{displayBadgeText}</div>}

                    <div className={styles.content}>
                        <div className={styles.imageContainer}>
                            <Image
                                src={currentImageSrc}
                                alt={imageAlt}
                                width={100}
                                height={100}
                                layout="responsive"
                                className={styles.image}
                            />
                        </div>

                        <div className={styles.details}>
                            <Typography variant="title-XS" as="h3" className={styles.title}>
                                {title}
                            </Typography>
                            <Typography
                                variant="paragraph-XS"
                                as="p"
                                className={styles.description}
                            >
                                {description}
                            </Typography>
                            {redeemLabel && variant !== 'lite' && (
                                <Button
                                    variant="primary"
                                    size="large"
                                    disabled={disabledButton}
                                    className={styles.redeemButton}
                                    onClick={handleRedeem}
                                    aria-label={`${redeemLabel} ${title}`}
                                    type="button"
                                >
                                    {redeemLabel}
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className={styles.dashedLine} aria-hidden="true"></div>

                    <div className={styles.footer}>
                        <p className={styles.expiryDate}>Udløber den {expiryDate}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VouchersCard;
